import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { AppShell } from '~/components/layout/app-shell'
import { EmptyState } from '~/components/shared/empty-state'
import { ErrorBanner } from '~/components/ui/error-banner'
import { MessageBubble } from '~/components/chat/message-bubble'
import { CitationCard } from '~/components/chat/citation-card'
import { getChatMessages } from '~/api/chat-history'
import { useAsyncResource } from '~/hooks/use-async'
import type { ChatMessage } from '~/hooks/use-chat-stream'
import type { StoredChatMessage } from '~/api/types'

/** 저장된 메시지(DTO)를 버블이 쓰는 ChatMessage로 변환한다. */
function toChatMessages(stored: StoredChatMessage[]): ChatMessage[] {
    return stored.map((m, i) => ({
        id: `${m.createdAt}-${i}`,
        role: m.role,
        content: m.content,
        // 저장된 근거를 보존해 우측 근거 패널을 재현한다.
        citations: m.citations,
    }))
}

/** 지난 대화 보기. GET /chat/sessions/{id}/messages를 읽기 전용으로 재생한다. */
export function SessionPage() {
    const { id } = useParams<{ id: string }>()
    const { data, loading, error } = useAsyncResource(
        () => (id ? getChatMessages(id) : Promise.resolve<StoredChatMessage[]>([])),
        [id],
        '대화 이력을 불러오지 못했어요.'
    )

    const messages = data ? toChatMessages(data) : []
    // 메시지에 실린 근거를 모아 우측 패널을 구성한다(채팅 패널과 동일한 레이아웃).
    const citations = messages.flatMap((m) => m.citations ?? [])

    return (
        <AppShell>
            <div className='mx-auto flex h-full w-full max-w-5xl flex-col px-4 py-6 lg:px-8'>
                <div className='mb-4 flex shrink-0 items-center justify-between gap-4'>
                    <h1 className='text-foreground text-lg font-semibold'>
                        지난 대화
                    </h1>
                    <Link
                        to='/consultation/history'
                        className='text-muted-foreground hover:text-primary inline-flex items-center gap-1 text-sm transition-colors'>
                        <ArrowLeft className='size-4' />
                        상담 이력
                    </Link>
                </div>

                {error ? (
                    <ErrorBanner className='mb-3 shrink-0'>{error}</ErrorBanner>
                ) : null}

                <div className='grid min-h-0 flex-1 gap-4 lg:grid-cols-[1fr_320px]'>
                    <div className='border-border bg-background min-h-0 overflow-y-auto rounded-md border p-4'>
                        {loading ? (
                            <p className='text-muted-foreground text-sm'>
                                대화를 불러오는 중…
                            </p>
                        ) : messages.length === 0 ? (
                            <EmptyState
                                title='대화 내용이 없어요'
                                description='이 세션에는 표시할 메시지가 없어요.'
                            />
                        ) : (
                            <div className='space-y-3'>
                                {messages.map((m) => (
                                    <MessageBubble key={m.id} message={m} />
                                ))}
                            </div>
                        )}
                    </div>

                    <aside className='hidden min-h-0 flex-col gap-3 overflow-y-auto lg:flex'>
                        <h2 className='text-foreground text-sm font-semibold'>
                            근거 자료
                        </h2>
                        {citations.length === 0 ? (
                            <p className='text-muted-foreground text-sm'>
                                이 대화에 사용된 근거가 여기에 표시돼요.
                            </p>
                        ) : (
                            citations.map((c, i) => (
                                <CitationCard
                                    key={`${c.documentId}-${i}`}
                                    citation={c}
                                    index={i}
                                />
                            ))
                        )}
                    </aside>
                </div>
            </div>
        </AppShell>
    )
}
